/**
 * @module linkage/chartTypeSwitch/reconcileForChartType/reconcileSort
 * @description 切图 L2 — 排序：仅当排序子项不可见时回退（整块隐藏保留值，靠 L1 formDelKey 过滤上传）
 */

import { ChartType, type TChartType } from '@dbl/core/chart';
import {
  horizontalBarYAxisItemsVisibility,
  isXAxisSettingVisible,
  isYAxisSettingVisible,
  eventLineXAxisItemsVisibility,
  lineFamilyXAxisItemsVisibility,
  mixedChartXAxisItemsVisibility,
  xAxisSettingItemsVisibility,
} from '../../../manifest/visibility/visibilityFactories';
import type { QBStateSnapshot } from '../../../types';
import { applyLayerPatch } from './applyLayerPatch';
import type { IReconcileLayerResult } from './reconcileTypes';

const SortUiMode = {
  Default: 'default',
  Metric: 'metric',
  Manual: 'manual',
  DatasetField: 'datasetField',
} as const;

type TSortUiMode = (typeof SortUiMode)[keyof typeof SortUiMode];

function toSortUiMode(sortBy: string | undefined): TSortUiMode {
  if (!sortBy || sortBy === 'default' || sortBy === 'defaultSort') {
    return SortUiMode.Default;
  }
  if (sortBy === 'x-axis' || sortBy === 'manual') {
    return SortUiMode.Manual;
  }
  if (sortBy === 'datasetField' || sortBy === 'datasetFieldSort') {
    return SortUiMode.DatasetField;
  }
  if (
    sortBy === 'metric'
    || sortBy === 'customMetricSort'
    || sortBy.endsWith('-asc')
    || sortBy.endsWith('-desc')
  ) {
    return SortUiMode.Metric;
  }
  return SortUiMode.Default;
}

function buildDefaultSortPayload(): Record<string, unknown> {
  return {
    sortBy: 'default',
    chartSorts: undefined,
    customMetricSort: undefined,
    xAxisValues: undefined,
  };
}

const SORT_UPLOAD_KEYS = [
  'sortBy',
  'chartSorts',
  'customMetricSort',
  'xAxisValues',
] as const;

export interface ISortItemVisibility {
  panelVisible: boolean;
  metricSortVisible: boolean;
  manualSortVisible: boolean;
  datasetFieldSortVisible: boolean;
}

const HIDDEN_SORT_VISIBILITY: ISortItemVisibility = {
  panelVisible: false,
  metricSortVisible: false,
  manualSortVisible: false,
  datasetFieldSortVisible: false,
};

function resolveItemsVisibility(
  items: Record<string, (qb: QBStateSnapshot) => boolean>,
  qb: QBStateSnapshot,
): ISortItemVisibility {
  return {
    panelVisible: !!items.SortBy?.(qb),
    metricSortVisible: !!items.MetricSort?.(qb),
    manualSortVisible: !!items.ManualSort?.(qb),
    datasetFieldSortVisible: !!items.DatasetFieldSort?.(qb),
  };
}

export function resolveSortItemVisibility(
  chartType: TChartType,
  qb: QBStateSnapshot,
): ISortItemVisibility {
  if (chartType === ChartType.HorizontalBarChart) {
    if (!isYAxisSettingVisible(qb)) {
      return HIDDEN_SORT_VISIBILITY;
    }
    return resolveItemsVisibility(horizontalBarYAxisItemsVisibility, qb);
  }

  if (!isXAxisSettingVisible(qb)) {
    return HIDDEN_SORT_VISIBILITY;
  }

  if (
    chartType === ChartType.Line
    || chartType === ChartType.PercentLine
  ) {
    return resolveItemsVisibility(lineFamilyXAxisItemsVisibility, qb);
  }
  if (chartType === ChartType.EventLine) {
    return resolveItemsVisibility(eventLineXAxisItemsVisibility, qb);
  }
  if (chartType === ChartType.MixedChart) {
    return resolveItemsVisibility(mixedChartXAxisItemsVisibility, qb);
  }

  return resolveItemsVisibility(xAxisSettingItemsVisibility, qb);
}

function readEffectiveSortBy(
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
): string | undefined {
  if ('sortBy' in draft) {
    return draft.sortBy as string | undefined;
  }
  return base.sortBy as string | undefined;
}

/**
 * 整块排序隐藏时不改 base/draft；仅子项（指标/手动/字段排序）在新图不可见时重置。
 */
function shouldResetSort(
  sortBy: string | undefined,
  vis: ISortItemVisibility,
): boolean {
  if (!vis.panelVisible) {
    return false;
  }
  const mode = toSortUiMode(sortBy);
  if (mode === SortUiMode.Metric && !vis.metricSortVisible) {
    return true;
  }
  if (mode === SortUiMode.Manual && !vis.manualSortVisible) {
    return true;
  }
  if (mode === SortUiMode.DatasetField && !vis.datasetFieldSortVisible) {
    return true;
  }
  return false;
}

function buildSortPatches(
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
  nextBase: Record<string, unknown>,
  nextDraft: Record<string, unknown>,
): Pick<IReconcileLayerResult, 'basePatch' | 'draftPatch'> {
  const basePatch: Record<string, unknown> = {};
  const draftPatch: Record<string, unknown> = {};
  for (const key of SORT_UPLOAD_KEYS) {
    if (nextBase[key] !== base[key]) {
      basePatch[key] = nextBase[key];
    }
    if (nextDraft[key] !== draft[key]) {
      draftPatch[key] = nextDraft[key];
    }
  }
  return { basePatch, draftPatch };
}

export function reconcileSort(
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
  vis: ISortItemVisibility,
): IReconcileLayerResult {
  const sortBy = readEffectiveSortBy(base, draft);
  if (!shouldResetSort(sortBy, vis)) {
    return { base, draft, basePatch: {}, draftPatch: {} };
  }

  const resetPatch = { ...buildDefaultSortPayload() };
  const nextBase = applyLayerPatch(base, resetPatch);
  const nextDraft = applyLayerPatch(draft, resetPatch);
  const { basePatch, draftPatch } = buildSortPatches(
    base,
    draft,
    nextBase,
    nextDraft,
  );

  return {
    base: nextBase,
    draft: nextDraft,
    basePatch,
    draftPatch,
  };
}

export function reconcileSortForChartType(
  chartType: TChartType,
  qb: QBStateSnapshot,
  base: Record<string, unknown>,
  draft: Record<string, unknown>,
): IReconcileLayerResult {
  const sortVis = resolveSortItemVisibility(chartType, qb);
  return reconcileSort(base, draft, sortVis);
}
